/**
 * Email formatting utilities that mirror backend Formatters::Email behavior
 */

// Email validation regex patterns
// https://stackoverflow.com/a/201378/24249020
export const emailRegex =
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

// Regex for email name part (everything before @)
// Matches the entire string to ensure it contains only valid email name characters
export const emailNameRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;

/**
 * Trims whitespace and converts to lowercase
 */
export function normalizeEmail(email: string): string {
  if (!email) return '';
  return email.trim().toLowerCase();
}

/**
 * Parse email with domain completion for team domains
 */
export function parseAndNormalizeEmail(
  input: string,
  teamDomain?: string,
): string {
  if (!input) return '';

  const trimmedInput = input.trim();

  // Apply domain completion if needed
  const completedEmail =
    trimmedInput.includes('@') || !teamDomain
      ? trimmedInput
      : `${trimmedInput}@${teamDomain.toLowerCase()}`;

  return normalizeEmail(completedEmail);
}

/**
 * Deduplicate by normalized email
 * Keeps the first occurrence of each unique email
 * Useful for bulk operations
 */
export function deduplicateEmails(emails: string[]): string[] {
  const uniqueEmails = new Set<string>();
  const deduplicatedEmails: string[] = [];

  for (const email of emails) {
    const normalized = normalizeEmail(email);
    if (normalized && !uniqueEmails.has(normalized)) {
      uniqueEmails.add(normalized);
      deduplicatedEmails.push(email); // Keep original format for display
    }
  }

  return deduplicatedEmails;
}

/**
 * Check if two email addresses are the same after normalization
 */
export function emailsMatch(email1: string, email2: string): boolean {
  return normalizeEmail(email1) === normalizeEmail(email2);
}

/**
 * Validate if a string is a complete email address
 */
export function isValidEmail(input: string): boolean {
  if (!input) return false;
  return emailRegex.test(input.toLowerCase());
}

/**
 * Validate if a string is a valid email name part (before @)
 * Useful for domain completion scenarios
 */
export function isValidEmailName(input: string): boolean {
  if (!input) return false;
  return emailNameRegex.test(input.toLowerCase()) && !input.includes('@');
}

/**
 * Get the domain of an email address
 */
export function getEmailDomain(email: string): string | undefined {
  if (!isValidEmail(email)) return;
  return normalizeEmail(email).split('@').pop();
}
